export const SLIDER_TYPE = {
  Normal: 'Slider',
  QuestionMark: 'SliderQuestionMark',
  Cloud: 'SliderCloud',
  Neither: 'SliderNeither',
  Both: 'SliderBoth',
  Bar: 'Bar',
  TextAgender: 'TextAgender',
  TextNone: 'TextNone',
  All: 'All',
} as const

export type SliderType = typeof SLIDER_TYPE[keyof typeof SLIDER_TYPE]

export type GenderResult =
  | {
      id: string
      name: string
      catchphrase: string
      description: string
      magazineUrl: string
      summary: string
      sliderType?: SliderType
      isOthers?: boolean
      mascot?: string
    }
  | {
      id: string
      name: string
      catchphrase: string
      description: string
      magazineUrl: string
      summary?: string
      sliderType?: SliderType
      isOthers?: boolean
      mascot?: string
    }
export class Gender {
  readonly genderId: string

  constructor(genderId: string | undefined) {
    this.genderId = genderId || ''
  }

  gender(): GenderResult {
    return Gender.list()[this.genderId]
  }

  static list(): { [genderId: string]: GenderResult } {
    return {
      male: {
        id: 'male',
        name: '',
        catchphrase: '',
        description: '',
        magazineUrl: '',
      },
      female: {
        id: 'female',
        name: '',
        catchphrase: '',
        description: '',
        magazineUrl: '',
      },
      cisgender_male: {
        id: 'cisgender_male',
        name: 'シスジェンダー男性',
        catchphrase: '私は、「こころの性」と「からだの性」が同じ',
        description:
          '「自分は“トランスジェンダーではない”けど、これに名前あるのかな？」こちら、耳慣れない概念かもしれません。生まれたときに「男性／女性」と診断されて以来、その性として生活することに違和感がない人を「シスジェンダー」と呼びます。',
        magazineUrl: 'https://jobrainbow.jp/magazine/cisgender',
      },
      cisgender_female: {
        id: 'cisgender_female',
        name: 'シスジェンダー女性',
        catchphrase: '私は、「こころの性」と「からだの性」が同じ',
        description:
          '「自分は“トランスジェンダーではない”けど、これに名前あるのかな？」こちら、耳慣れない概念かもしれません。生まれたときに「男性／女性」と診断されて以来、その性として生活することに違和感がない人を「シスジェンダー」と呼びます。',
        magazineUrl: 'https://jobrainbow.jp/magazine/cisgender',
      },
      transgender_female: {
        id: 'transgender_female',
        name: 'トランスジェンダー女性(MtF)',
        catchphrase: '私は、「こころの性」と「からだの性」がぴったり同じ、と感じない',
        description:
          'トランスジェンダーのあり方も様々です。「戸籍上の性別で扱われることが嫌だ」「性別適合手術を早く受けたい」とはっきり認識しているとは限らず、「性同一性障害と診断されてはいないものの、自分の性別にモヤモヤするんだよな」なんて方もいます。',
        magazineUrl: 'https://jobrainbow.jp/magazine/transgid',
      },
      transgender_male: {
        id: 'transgender_male',
        name: 'トランスジェンダー男性(FtM)',
        catchphrase: '私は、「こころの性」と「からだの性」がぴったり同じ、と感じない',
        description:
          'トランスジェンダーのあり方も様々です。「戸籍上の性別で扱われることが嫌だ」「性別適合手術を早く受けたい」とはっきり認識しているとは限らず、「性同一性障害と診断されてはいないものの、自分の性別にモヤモヤするんだよな」なんて方もいます。',
        magazineUrl: 'https://jobrainbow.jp/magazine/transgid',
      },
      xgender_neutral: {
        id: 'xgender_neutral',
        name: 'Xジェンダー(中性)',
        catchphrase: '私は、自分のことを「男性」とも「女性」とも言いたくない',
        description:
          '「自分には男性の要素も女性の要素もない」「男性と女性のあいだくらい、って考えるとしっくりくる」「いや、私は男性でもあるし女性でもある！」さまざまな形がありますが、いずれにせよ自分を「男性／女性」と断言することに抵抗があるあなたは、Xジェンダーかもしれません。',
        magazineUrl: 'https://jobrainbow.jp/magazine/xgender',
      },
      xgender_agender: {
        id: 'xgender_agender',
        name: 'Xジェンダー(無性)',
        catchphrase: '私は、自分のことを「男性」とも「女性」とも言いたくない',
        description:
          '「自分には男性の要素も女性の要素もない」「男性と女性のあいだくらい、って考えるとしっくりくる」「いや、私は男性でもあるし女性でもある！」さまざまな形がありますが、いずれにせよ自分を「男性／女性」と断言することに抵抗があるあなたは、Xジェンダーかもしれません。',
        magazineUrl: 'https://jobrainbow.jp/magazine/xgender',
        sliderType: SLIDER_TYPE.TextAgender,
      },
      xgender_bigender: {
        id: 'xgender_bigender',
        name: 'Xジェンダー(両性)',
        catchphrase: '私は、自分のことを「男性」とも「女性」とも言いたくない',
        description:
          '「自分には男性の要素も女性の要素もない」「男性と女性のあいだくらい、って考えるとしっくりくる」「いや、私は男性でもあるし女性でもある！」さまざまな形がありますが、いずれにせよ自分を「男性／女性」と断言することに抵抗があるあなたは、Xジェンダーかもしれません。',
        magazineUrl: 'https://jobrainbow.jp/magazine/xgender',
        sliderType: SLIDER_TYPE.Both,
      },
      genderfluid: {
        id: 'genderfluid',
        name: 'ジェンダーフルイド',
        catchphrase: '私は、「こころの性」が時と場合によって変わる気がする',
        description:
          '「昨日は自分を男性だと感じていたけれど、今朝起きてみると違和感がある……先週はたしかに自分は女性だと感じていたし、自分の性ってなんなんだろう？」こんな違和感と向き合うあなたは、もしかしたらジェンダーフルイドかもしれません。揺れ動くこころの性を持っているのだって、一つの性のあり方です。',
        magazineUrl: 'https://jobrainbow.jp/magazine/gender-fluid',
        sliderType: SLIDER_TYPE.Cloud,
      },
      nonbinary: {
        id: 'nonbinary',
        name: 'ノンバイナリー',
        catchphrase: '私は、「男性／女性らしい」と言われない外見・言動をしたい',
        description:
          '「”髭を伸ばす”とかに興味はないし、かといってかわいいメイクにも興味ないな……」「シンプルなユニセックスのファッションが好きだな」「こころの性」と「ふるまう性」は別ものです。服装や口調で「男性／女性的」と言われるのが嫌だったりしませんか？',
        magazineUrl: 'https://jobrainbow.jp/magazine/whatisnonbinary',
      },
      crossdresser_male: {
        id: 'crossdresser_male',
        name: 'クロスドレッサー',
        catchphrase: '私は、異性装をしたい・していると自分らしい気がして落ち着く',
        description:
          '「自分は男性で、”女性になりたい”わけじゃない。でも、女性の服装をしているのが落ち着く」「普段はレディースのファッションやメイクだけど、気合を入れたい時はメンズライクな服装」自分の性別に違和感はない。でも「男性だからスーツ／女性だからスカート」などと求められる服装には不満があるし、それとは異なる自分でいたい。そんなあなたはクロスドレッサーかも？',
        magazineUrl: 'https://jobrainbow.jp/magazine/transvestite',
      },
      crossdresser_female: {
        id: 'crossdresser_female',
        name: 'クロスドレッサー',
        catchphrase: '私は、異性装をしたい・していると自分らしい気がして落ち着く',
        description:
          '「自分は男性で、”女性になりたい”わけじゃない。でも、女性の服装をしているのが落ち着く」「普段はレディースのファッションやメイクだけど、気合を入れたい時はメンズライクな服装」自分の性別に違和感はない。でも「男性だからスーツ／女性だからスカート」などと求められる服装には不満があるし、それとは異なる自分でいたい。そんなあなたはクロスドレッサーかも？',
        magazineUrl: 'https://jobrainbow.jp/magazine/transvestite',
      },
      fictosexual: {
        id: 'fictosexual',
        name: 'フィクトセクシュアル',
        catchphrase: '私は、アニメや漫画などに登場するキャラクターに恋愛感情や性的な感情を持つ',
        description:
          'キャラクターが現実に存在していないことはわかっている上で、私は実際にいる人物ではなくこのキャラクターに恋している。なのに「二次元に逃げている」みたいに揶揄されるなんて意味わからない……そんな苦い経験のあるあなたは、フィクトセクシュアルかもしれません。',
        magazineUrl: 'https://jobrainbow.jp/magazine/fictosexual',
        summary: '二次元が好き',
        isOthers: true,
        mascot: 'crocodile',
        sliderType: SLIDER_TYPE.Bar,
      },
      aromantic: {
        id: 'aromantic',
        name: 'アロマンティック',
        catchphrase: '私は、他人に恋心を抱かない',
        description:
          '「自分は絶対に人に恋することはない」「そういえば、人を好きになったことないな」世の中のドラマから漂う「恋愛至上主義」にげんなりしたり、「恋人いないの？」という質問に「恋人いなくちゃダメなの……？」と困ったりした経験、ありませんか？',
        magazineUrl: 'https://jobrainbow.jp/magazine/aromantic',
        sliderType: SLIDER_TYPE.TextNone,
        mascot: 'sheep',
      },
      panromantic: {
        id: 'panromantic',
        name: 'パンロマンティック',
        catchphrase: '私は、誰かを好きになるとき、その人の性別なんて関係ない',
        description:
          '「好きになる相手が男性だ・女性だ、とか考えたことなかった」「今まで自分はバイセクシュアルだと思っていたけれど、“男性も女性も好きになる”というよりは“相手の性別とか関係ない”の方が近い気がする」こう感じたあなたは、もしかしたら「パンロマンティック」がしっくりくるかもしれません。',
        magazineUrl: 'https://jobrainbow.jp/magazine/pansexual',
        sliderType: SLIDER_TYPE.All,
        mascot: 'poodle',
      },
      heteroromantic_male: {
        id: 'heteroromantic_male',
        name: 'ヘテロロマンティック',
        catchphrase: '私は、異性を好きになる',
        description:
          '普段あまり意識されませんが、異性愛だってセクシュアリティの１つです。「ゲイ・レズビアン・バイセクシュアルとかは聞いたことあったけど、自分みたいに異性を好きになる人にも名前あるんだ」と驚いた方もいるのではないでしょうか。',
        magazineUrl: 'https://jobrainbow.jp/magazine/heterosexual',
        mascot: 'hedgehog',
      },
      heteroromantic_female: {
        id: 'heteroromantic_female',
        name: 'ヘテロロマンティック',
        catchphrase: '私は、異性を好きになる',
        description:
          '普段あまり意識されませんが、異性愛だってセクシュアリティの１つです。「ゲイ・レズビアン・バイセクシュアルとかは聞いたことあったけど、自分みたいに異性を好きになる人にも名前あるんだ」と驚いた方もいるのではないでしょうか。',
        magazineUrl: 'https://jobrainbow.jp/magazine/heterosexual',
        mascot: 'hedgehog',
      },
      maromantic: {
        id: 'maromantic',
        name: 'マロマンティック',
        catchphrase: '私は、自分が男性か女性かはともかく、男性を好きになる',
        description:
          '「私が好きになる相手は男性、でもそもそも自分を男性／女性って表すのがピンとこないから”同性愛””異性愛”ってのもなんだかなぁ……」というあなたは、もしかしたら「異性愛・同性愛」よりも「マロマンティック」にしっくりくるかもしれません。',
        magazineUrl: 'https://jobrainbow.jp/magazine/mawomasexual',
        mascot: 'giraffe',
      },
      womaromantic: {
        id: 'womaromantic',
        name: 'ウーマロマンティック',
        catchphrase: '私は、自分が男性か女性かはともかく、女性を好きになる',
        description:
          '「私が好きになる相手は女性、でもそもそも自分を男性／女性って表すのがピンとこないから”同性愛””異性愛”ってのもなんだかなぁ……」というあなたは、もしかしたら「異性愛・同性愛」よりも「ウーマロマンティック」がしっくりくるかもしれません。',
        magazineUrl: 'https://jobrainbow.jp/magazine/mawomasexual',
        mascot: 'duck',
      },
      biromantic: {
        id: 'biromantic',
        name: 'バイロマンティック',
        catchphrase: '私は、男性も女性も好きになる',
        description:
          '「私は男性も女性も好きだな」と思う、バイロマンティック。バイロマンティックだから男女同じくらい好きになる、というわけではありません。「どっちも好きにはなるけれど、どちらかと言えば男性／女性の方が好き」という方もいます。',
        magazineUrl: 'https://jobrainbow.jp/magazine/bisexual',
        mascot: 'owl',
      },
      lesbian: {
        id: 'lesbian',
        name: 'レズビアン',
        catchphrase: '私は、女性として女性を愛する',
        description:
          '「ボーイッシュ」「サバサバしている」……別にこれは「レズビアンの条件」ではありません。「からだの性」が男性か女性かなどは関係なく、「自分は女性として女性を愛している」。それだけでいいんです。',
        magazineUrl: 'https://jobrainbow.jp/magazine/lesbian',
      },
      gay: {
        id: 'gay',
        name: 'ゲイ',
        catchphrase: '私は、男性として男性を愛する',
        description:
          '「筋肉質」「センスがいい」「”女性らしい”言葉遣い」……そんなイメージをお持ちの方もいらっしゃるかもしれません。「からだの性」が男性か女性かなどは関係なく、「自分は男性として男性を愛している」。それだけでいいんです。',
        magazineUrl: 'https://jobrainbow.jp/magazine/whatisgay',
      },
      questioning: {
        id: 'questioning',
        name: 'クエスチョニング',
        catchphrase: '私は、自分や好きになる相手の性について考え中',
        description:
          '「自分の性別は、男性か女性か、はたまたどちらでもないのか……」「私が好きになるのって、どんなセクシュアリティの人だろう」と自分探しの道半ばにいることを焦る必要はありません。このクエスチョニングもまた、１つの性のあり方です。',
        magazineUrl: 'https://jobrainbow.jp/magazine/questioning',
        sliderType: SLIDER_TYPE.QuestionMark,
      },
      demiromantic: {
        id: 'demiromantic',
        name: 'デミロマンティック',
        catchphrase: '私は、親友レベルに仲のいい人にだけ恋愛感情を持つ',
        description:
          '「一目惚れってよくわからない」「今までパートナーになった人たちは、みんな友達として仲良くなってから恋愛関係を築いてきたな」……これらに共感するあなたは、デミロマンティックかもしれません。あなたの恋愛感情の根底には、深い絆があるのではないでしょうか？',
        magazineUrl: 'https://jobrainbow.jp/magazine/demiromantic',
        summary: '絆が大事',
        isOthers: true,
        mascot: 'elephant',
        sliderType: SLIDER_TYPE.Bar,
      },
      sapioromantic: {
        id: 'sapioromantic',
        name: 'サピオロマンティック',
        catchphrase: '私は、何より相手の知性が好き',
        description:
          '「頭がいい人が好き」「賢いな、と思える相手にしか魅力を感じない」なんて経験があるあなたは、もしかしたらサピオロマンティックかも。ルックスよりもロジカルな話し方にドキドキしたこと、ありませんか？',
        magazineUrl: 'https://jobrainbow.jp/magazine/sapio',
        summary: '知性が大事',
        isOthers: true,
        mascot: 'koala',
        sliderType: SLIDER_TYPE.Bar,
      },
      lithromantic: {
        id: 'lithromantic',
        name: 'リスロマンティック',
        catchphrase: '私は、人のことを好きにはなるけれど、好かれたいとは思えない',
        description:
          '「◯◯さんのことは好きだけど、両想いにはなりたくないんだよね」と友人に相談したら「何それ？意味わかんない」と突っぱねられた苦い経験や、素敵だと思っていた相手からいざ好意を向けられたら、なんだか居心地が悪い・気持ち悪く思えてしまう、なんてことありませんか？そんなあなたは、リスロマンティックかもしれません。',
        magazineUrl: 'https://jobrainbow.jp/magazine/lithromantic',
        summary: '両想いはいらない',
        isOthers: true,
        mascot: 'pelican',
        sliderType: SLIDER_TYPE.Bar,
      },
      quoisexual: {
        id: 'quoisexual',
        name: 'クワセクシュアル',
        catchphrase: '私は、友情・恋愛感情・性的な感情をはっきり区別できない',
        description:
          'このセクシュアリティ診断に回答してきたなかで、「恋愛感情とか性的な感情ってはっきりこれって言えなくない？」と思ったあなたこそ、まさにクワセクシュアルかもしれません。そもそも感情は明確に線引きできるものではないため、「自分が納得できている」ことが重要でしょう。しかし、「まだ整理できていない……」と焦る必要はありません。それもまた、1つのセクシュアリティです。',
        magazineUrl: 'https://jobrainbow.jp/magazine/whatisquoisexual',
        sliderType: SLIDER_TYPE.Bar,
        isOthers: true,
        mascot: 'bear',
      },
      asexual: {
        id: 'asexual',
        name: 'アセクシュアル',
        catchphrase: '私は、誰かに性的魅力を感じたことがない',
        description:
          '「性行為したいと思った相手がいない」と言ったら、「好きな人とは性行為をして当たり前でしょ」と言われてもやもやする……そんなあなたはアセクシュアルかもしれません。「自分がおかしいのかな？」なんて責める必要はありませんよ。',
        magazineUrl: 'https://jobrainbow.jp/magazine/asexual',
        sliderType: SLIDER_TYPE.TextNone,
      },
      pansexual: {
        id: 'pansexual',
        name: 'パンセクシュアル',
        catchphrase: '私は、性的魅力を感じる相手の性別なんてどうでもいい',
        description:
          '「性的興奮を抱く相手が男性だ・女性だ、とか考えたことなかった」「今まで自分はバイセクシュアルだと思っていたけれど、“男性も女性も好きになる”というよりは“相手の性別とか関係ない”の方が近い気がする」こう感じたあなたは、「パンセクシュアル」の方がしっくりくるかもしれません。',
        magazineUrl: 'https://jobrainbow.jp/magazine/pansexual',
        sliderType: SLIDER_TYPE.All,
      },
      heterosexual_male: {
        id: 'heterosexual_male',
        name: 'ヘテロセクシュアル',
        catchphrase: '私が性的魅力を感じる相手は異性',
        description:
          '「私は男性として、女性に性的魅力を感じる」「私は女性として、男性に性的魅力を感じる」そんなあなたは、ヘテロセクシュアルです。性的感情を異性に抱くことは「あたりまえ」として無視されてしまいがちですが、これもまた1つの性のあり方です。当然他のセクシュアリティと優劣はありませんし、名前だってあるんですよ。',
        magazineUrl: 'https://jobrainbow.jp/magazine/heterosexual',
      },
      heterosexual_female: {
        id: 'heterosexual_female',
        name: 'ヘテロセクシュアル',
        catchphrase: '私が性的魅力を感じる相手は異性',
        description:
          '「私は男性として、女性に性的魅力を感じる」「私は女性として、男性に性的魅力を感じる」そんなあなたは、ヘテロセクシュアルです。性的感情を異性に抱くことは「あたりまえ」として無視されてしまいがちですが、これもまた1つの性のあり方です。当然他のセクシュアリティと優劣はありませんし、名前だってあるんですよ。',
        magazineUrl: 'https://jobrainbow.jp/magazine/heterosexual',
      },
      bisexual: {
        id: 'bisexual',
        name: 'バイセクシュアル',
        catchphrase: '私が性的魅力を感じるのは、男性と女性の両方',
        description:
          '男性と女性の両方に性的魅力を感じるバイセクシュアル。漠然と「男性・女性のどちらかだけに性的魅力を感じるわけじゃないな」と感じているあなたも、もしかしたらバイセクシュアルかもしれません。ただ、もしあなたが「性別関係なく人を好きになるなぁ」と感じているのであれば、「パンロマンティック」の方がしっくりくるかも？',
        magazineUrl: 'https://jobrainbow.jp/magazine/bisexual',
      },
      masexual: {
        id: 'masexual',
        name: 'マセクシュアル',
        catchphrase: '私が男性か女性かはともかく、性的魅力を感じるのは男性',
        description:
          '「私が性的魅力を感じる相手、性行為したいと思うのは男性だけど、自分の性別は男性・女性って断言できないんだよなぁ……」というあなたは、マセクシュアルがしっくりくるかもしれません。',
        magazineUrl: 'https://jobrainbow.jp/magazine/mawomasexual',
      },
      womasexual: {
        id: 'womasexual',
        name: 'ウーマセクシュアル',
        catchphrase: '私が男性か女性かはともかく、性的魅力を感じる相手は女性',
        description:
          '「私が性的魅力を感じる相手、性行為したいと思うのは女性だけど、自分の性別は男性・女性って断言できないんだよなぁ……」というあなたは、ウーマセクシュアルがしっくりくるかもしれません。',
        magazineUrl: 'https://jobrainbow.jp/magazine/mawomasexual',
      },
      demisexual: {
        id: 'demisexual',
        name: 'デミセクシュアル',
        catchphrase: '私は、本当に親しい間柄の人にしか性的な魅力を感じない',
        description:
          '「出会ったばかりの人と肉体関係を持つ感覚がよくわからない」「今まで性的な関係になった人は親友だけかも」思い当たるあなたは、デミセクシュアルかもしれません。精神的に深い繋がりを持った人としか肉体の繋がりを持ちたくない、と日頃から感じていたりしませんか？',
        magazineUrl: 'https://jobrainbow.jp/magazine/demisexual',
        summary: '絆が大事',
        isOthers: true,
        mascot: 'elephant',
        sliderType: SLIDER_TYPE.Bar,
      },
      sapiosexual: {
        id: 'sapiosexual',
        name: 'サピオセクシュアル',
        catchphrase: '私は、何よりも相手の知性に性的な魅力を感じる',
        description:
          '「ルックスよりも、相手の深い知識に興奮する」「性的な繋がりを持つ相手は、頭の回転が速いなと感じる人がいい」そんなあなたは、サピオセクシュアルかもしれません。今まで好きになった相手を思い出した時、共通した特徴としてまっさきに思い浮かぶのが「知性」だったりしませんか？',
        magazineUrl: 'https://jobrainbow.jp/magazine/sapio',
        summary: '知性が大事',
        isOthers: true,
        mascot: 'koala',
        sliderType: SLIDER_TYPE.Bar,
      },
      lithsexual: {
        id: 'lithsexual',
        name: 'リスセクシュアル',
        catchphrase: '私は、人に性的な魅力を感じるけれど、性的な目で見られたくはない',
        description:
          '「性的な感情を否定するわけではないし自分も人にそういう感情を持つけど、持たれるのは嫌だなぁ」こんなことを感じるあなたは、リスセクシュアルかもしれません。例え好きな相手だろうと、性的な目で見られると不快になった、なんて経験はありませんか？',
        magazineUrl: 'https://jobrainbow.jp/magazine/lithsexual',
        summary: '両想いはいらない',
        isOthers: true,
        mascot: 'pelican',
        sliderType: SLIDER_TYPE.Bar,
      },
      noncrossdresswer_male: {
        id: 'male',
        name: '',
        catchphrase: '',
        description: '',
        magazineUrl: '',
      },
      noncrossdresser_female: {
        id: 'male',
        name: '',
        catchphrase: '',
        description: '',
        magazineUrl: '',
      },
      homosexual_female: {
        id: 'homosexual_female',
        name: 'ホモセクシュアル',
        catchphrase: '',
        description:
          '同性をセクシーに感じるなど、性的感情が同性に向くあなたはもしかしたらホモセクシュアルかもしれません。恋愛指向と性的指向は別ですから、必ずしも「同性に恋愛感情が向くから性的感情も同性だけに対して向く」とは限りません。',
        magazineUrl: 'https://jobrainbow.jp/magazine/homosexual-2',
        mascot: 'hedgehog',
      },
      homosexual_male: {
        id: 'homosexual_male',
        name: 'ホモセクシュアル',
        catchphrase: '',
        description:
          '同性をセクシーに感じるなど、性的感情が同性に向くあなたはもしかしたらホモセクシュアルかもしれません。恋愛指向と性的指向は別ですから、必ずしも「同性に恋愛感情が向くから性的感情も同性だけに対して向く」とは限りません。',
        magazineUrl: 'https://jobrainbow.jp/magazine/homosexual-2',
        mascot: 'hedgehog',
      },
      homoromantic_female: {
        id: 'homoromantic_female',
        name: 'ホモロマンティック',
        catchphrase: '',
        description:
          '同性に対してキュンとするなど、恋愛感情が同性に向くあなたはもしかしたらホモロマンティックかもしれません。恋愛感情と性的指向は別ですから、必ずしも「同性を好きになるから性的欲求も同性だけに対して向く」とは限りません。',
        magazineUrl: 'https://jobrainbow.jp/magazine/homosexual-2',
        mascot: 'hedgehog',
      },
      homoromantic_male: {
        id: 'homoromantic_male',
        name: 'ホモロマンティック',
        catchphrase: '',
        description:
          '同性に対してキュンとするなど、恋愛感情が同性に向くあなたはもしかしたらホモロマンティックかもしれません。恋愛感情と性的指向は別ですから、必ずしも「同性を好きになるから性的欲求も同性だけに対して向く」とは限りません。',
        magazineUrl: 'https://jobrainbow.jp/magazine/homosexual-2',
        mascot: 'hedgehog',
      },
    }
  }
}
